import React, { useState, useEffect } from 'react';
import { Card, DatePicker, Table, Space, message, Button, Checkbox, Popover } from 'antd';
import { DownloadOutlined, SettingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { useIsMobile } from '../hooks/useIsMobile';

const { RangePicker } = DatePicker;

function SettlementProfit() {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [summary, setSummary] = useState({ count: 0, total_actual: 0, total_reference: 0, total_profit: 0 });
  const [dateRange, setDateRange] = useState(null);
  const [visibleStatuses, setVisibleStatuses] = useState(new Set());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange && dateRange[0]) params.start_date = dateRange[0].format('YYYY-MM-DD');
      if (dateRange && dateRange[1]) params.end_date = dateRange[1].format('YYYY-MM-DD');

      const res = await api.get('/orders/settlement/profit', { params });
      if (res.code === 200) {
        setRawData(res.data.list);
        setStatuses(res.data.statuses || []);
        setSummary(res.data.summary);
        if (visibleStatuses.size === 0 && res.data.statuses) {
          setVisibleStatuses(new Set(res.data.statuses.map(s => s.id)));
        }
      }
    } catch (err) {}
    setLoading(false);
  };

  const pivotData = () => {
    const groups = {};
    rawData.forEach(row => {
      const key = `${row.team || ''}|${row.growth_method || ''}`;
      if (!groups[key]) {
        groups[key] = {
          team: row.team,
          growth_method: row.growth_method,
          statuses: {},
          totalCount: 0,
          totalActual: 0,
          totalReference: 0,
          totalProfit: 0
        };
      }
      const sid = row.status_id || 0;
      groups[key].statuses[sid] = {
        count: row.count,
        total_actual: Number(row.total_actual),
        total_reference: Number(row.total_reference),
        total_profit: Number(row.total_profit)
      };
      groups[key].totalCount += row.count;
      groups[key].totalActual += Number(row.total_actual);
      groups[key].totalReference += Number(row.total_reference);
      groups[key].totalProfit += Number(row.total_profit);
    });
    return Object.values(groups);
  };

  const pivoted = pivotData();

  const profitColor = (v) => v > 0 ? '#52c41a' : v < 0 ? '#ff4d4f' : '#000';

  const getColumns = () => {
    const cols = [
      { title: '小组', dataIndex: 'team', width: 120, fixed: 'left',
        onCell: (_, index) => {
          if (index === 0 || pivoted[index].team !== pivoted[index - 1].team) {
            let count = 1;
            for (let i = index + 1; i < pivoted.length && pivoted[i].team === pivoted[index].team; i++) count++;
            return { rowSpan: count };
          }
          return { rowSpan: 0 };
        }
      },
      { title: '涨粉方式', dataIndex: 'growth_method', width: 120, fixed: 'left' }
    ];

    statuses.filter(s => visibleStatuses.has(s.id)).forEach(s => {
      cols.push({
        title: s.name,
        children: [
          { title: '数量', width: 70, align: 'right', render: (_, record) => record.statuses[s.id]?.count || 0 },
          { title: '实际', width: 100, align: 'right', render: (_, record) => `¥${(record.statuses[s.id]?.total_actual || 0).toFixed(2)}` },
          { title: '参考', width: 100, align: 'right', render: (_, record) => `¥${(record.statuses[s.id]?.total_reference || 0).toFixed(2)}` },
          { title: '利润', width: 100, align: 'right', render: (_, record) => {
            const v = record.statuses[s.id]?.total_profit || 0;
            return <span style={{ color: profitColor(v) }}>¥{v.toFixed(2)}</span>;
          }}
        ]
      });
    });

    cols.push({
      title: '汇总',
      children: [
        { title: '数量', width: 80, align: 'right', render: (_, record) => record.totalCount },
        { title: '实际', width: 110, align: 'right', render: (_, record) => `¥${record.totalActual.toFixed(2)}` },
        { title: '参考', width: 110, align: 'right', render: (_, record) => `¥${record.totalReference.toFixed(2)}` },
        { title: '利润', width: 110, align: 'right', render: (_, record) => {
          const v = record.totalProfit;
          return <span style={{ color: profitColor(v), fontWeight: 'bold' }}>¥{v.toFixed(2)}</span>;
        }}
      ]
    });

    return cols;
  };

  const getSummaryRow = () => {
    const statusSummary = {};
    pivoted.forEach(row => {
      Object.entries(row.statuses).forEach(([sid, data]) => {
        if (!statusSummary[sid]) statusSummary[sid] = { count: 0, total_actual: 0, total_reference: 0, total_profit: 0 };
        statusSummary[sid].count += data.count;
        statusSummary[sid].total_actual += data.total_actual;
        statusSummary[sid].total_reference += data.total_reference;
        statusSummary[sid].total_profit += data.total_profit;
      });
    });

    const visibleStatusesList = statuses.filter(s => visibleStatuses.has(s.id));
    const visibleCount = visibleStatusesList.length;

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={2} align="right"><b>汇总</b></Table.Summary.Cell>
          {visibleStatusesList.map((s, i) => {
            const ss = statusSummary[s.id] || { count: 0, total_actual: 0, total_reference: 0, total_profit: 0 };
            return (
              <React.Fragment key={s.id}>
                <Table.Summary.Cell index={2 + i * 4} align="right"><b>{ss.count}</b></Table.Summary.Cell>
                <Table.Summary.Cell index={3 + i * 4} align="right"><b>¥{ss.total_actual.toFixed(2)}</b></Table.Summary.Cell>
                <Table.Summary.Cell index={4 + i * 4} align="right"><b>¥{ss.total_reference.toFixed(2)}</b></Table.Summary.Cell>
                <Table.Summary.Cell index={5 + i * 4} align="right"><b style={{ color: profitColor(ss.total_profit) }}>¥{ss.total_profit.toFixed(2)}</b></Table.Summary.Cell>
              </React.Fragment>
            );
          })}
          <Table.Summary.Cell index={2 + visibleCount * 4} align="right"><b>{summary.count}</b></Table.Summary.Cell>
          <Table.Summary.Cell index={3 + visibleCount * 4} align="right"><b>¥{Number(summary.total_actual).toFixed(2)}</b></Table.Summary.Cell>
          <Table.Summary.Cell index={4 + visibleCount * 4} align="right"><b>¥{Number(summary.total_reference).toFixed(2)}</b></Table.Summary.Cell>
          <Table.Summary.Cell index={5 + visibleCount * 4} align="right"><b style={{ color: profitColor(Number(summary.total_profit)) }}>¥{Number(summary.total_profit).toFixed(2)}</b></Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  const handleExport = () => {
    const headers = ['小组', '涨粉方式'];
    statuses.filter(s => visibleStatuses.has(s.id)).forEach(s => {
      headers.push(`${s.name}-数量`, `${s.name}-实际`, `${s.name}-参考`, `${s.name}-利润`);
    });
    headers.push('汇总-数量', '汇总-实际', '汇总-参考', '汇总-利润');

    const csvRows = [headers.join(',')];
    pivoted.forEach(row => {
      const vals = [`"${row.team || ''}"`, `"${row.growth_method || ''}"`];
      statuses.filter(s => visibleStatuses.has(s.id)).forEach(s => {
        const st = row.statuses[s.id];
        vals.push(st?.count || 0, (st?.total_actual || 0).toFixed(2), (st?.total_reference || 0).toFixed(2), (st?.total_profit || 0).toFixed(2));
      });
      vals.push(row.totalCount, row.totalActual.toFixed(2), row.totalReference.toFixed(2), row.totalProfit.toFixed(2));
      csvRows.push(vals.join(','));
    });

    const BOM = '﻿';
    const blob = new Blob([BOM + csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `利润表_${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success('导出成功');
  };

  const statusSelector = (
    <div style={{ maxWidth: 300 }}>
      <div style={{ marginBottom: 8, fontWeight: 'bold' }}>显示的完结状态：</div>
      <Checkbox
        checked={visibleStatuses.size === statuses.length}
        indeterminate={visibleStatuses.size > 0 && visibleStatuses.size < statuses.length}
        onChange={e => {
          if (e.target.checked) setVisibleStatuses(new Set(statuses.map(s => s.id)));
          else setVisibleStatuses(new Set());
        }}
        style={{ marginBottom: 8 }}
      >全选</Checkbox>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {statuses.map(s => (
          <Checkbox key={s.id} checked={visibleStatuses.has(s.id)}
            onChange={e => {
              const next = new Set(visibleStatuses);
              if (e.target.checked) next.add(s.id); else next.delete(s.id);
              setVisibleStatuses(next);
            }}
          >{s.name}</Checkbox>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <h3 style={{ margin: '0 0 12px 0' }}>利润表</h3>
      <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <RangePicker value={dateRange} onChange={v => setDateRange(v)} placeholder={['开始日期', '结束日期']} style={isMobile ? { width: '100%' } : {}} />
        <div style={{ display: 'flex', gap: 6 }}>
          <Button type="primary" onClick={loadData}>查询</Button>
          <Button onClick={() => { setDateRange(null); setTimeout(loadData, 100); }}>重置</Button>
          <Popover content={statusSelector} title={null} trigger="click" placement="bottomRight">
            <Button icon={<SettingOutlined />} />
          </Popover>
          <Button icon={<DownloadOutlined />} onClick={handleExport} />
        </div>
      </div>

      <Card>
        <Table
          rowKey={(_, i) => i}
          columns={getColumns()}
          dataSource={pivoted.map((r, i) => ({ ...r, key: i }))}
          loading={loading}
          pagination={false}
          scroll={{ x: 1200 }}
          bordered
          size="small"
          summary={getSummaryRow}
        />
      </Card>
    </div>
  );
}

export default SettlementProfit;
